import { DateTime } from 'luxon'

import { PagedResult } from '../DTOs/Common/PagedResult'
import { PlantDTO } from '../DTOs/Plants/PlantDTO'
import { PlantLookupDTO } from '../DTOs/Plants/PlantLookupDTO'
import { StoppageCalc } from '../DTOs/Stoppage/StoppageCalc'
import { StoppageDto } from '../DTOs/Stoppage/StoppageDto'
import { StoppageSubTypeDto } from '../DTOs/Stoppage/StoppageSubTypeDto'
import { StoppageTypeDto } from '../DTOs/Stoppage/StoppageTypeDto'
import { queryClient } from '../queryClient'
import type { TripleShift } from '../types/Shift'
import {
  appendCachedPlantStoppage,
  appendCachedStoppage,
  patchCachedOperationStatus,
} from './patchCachedStoppages'
import { stoppagesMutations } from './stoppagesMutations'

interface SubTypeOption {
  value: number
  label: string
  groupColor: string
  stoppageType: string
}

interface OfflineBatch {
  machineIds: number[]
  machines?: PlantLookupDTO[]
  stoppageTypeId: number
  stoppageSubTypeId: number
  timeIn: DateTime
  timeOut?: DateTime
  subType?: SubTypeOption
  selectedDate: DateTime
  selectedShift: TripleShift
}

function cachedPlants(): PlantLookupDTO[] {
  return queryClient.getQueryData<PlantLookupDTO[]>(['references', 'plantLookups']) ?? []
}

const BLAST_HOLE_DRILLING = 16
const DRILLING_METHODS = 8192 | 16384 | 32768

export function availableMachinesFromCache(
  selectedDate: DateTime,
  selectedShift: TripleShift,
  jobTypes?: number,
): PlantLookupDTO[] {
  // Note: picking blast-hole drilling takes in each drilling method, as JobFamilies.Covering does on the server.
  const covering = jobTypes && (jobTypes & BLAST_HOLE_DRILLING) ? jobTypes | DRILLING_METHODS : jobTypes
  for (const [, page] of queryClient.getQueriesData<PagedResult<StoppageCalc>>({
    queryKey: ['dataTable', 'operations', 'stoppageCalcs', selectedDate.toISODate(), selectedShift],
  })) {
    const rows = page?.data ?? []
    if (!rows.length) continue

    const plants = cachedPlants()

    return rows
      .filter(row => row.status === 'UP' && row.plantId)
      .map(row => plants.find(plant => plant.id === row.plantId)
        ?? ({ id: row.plantId, plantRef: row.machineName } as PlantLookupDTO))
      .filter(plant => !covering || plant.jobTypes == null || (plant.jobTypes & covering) !== 0)
  }
  return []
}

export async function addMultipleStoppagesOffline(batch: OfflineBatch) {
  const dateIso = batch.selectedDate.toISODate()!
  let added = 0

  for (const plantId of batch.machineIds) {
    const response = await stoppagesMutations.addStoppage(new StoppageDto({
      plantId,
      startTime: batch.timeIn,
      endTime: batch.timeOut,
      stoppageTypeId: batch.stoppageTypeId,
      stoppageSubTypeId: batch.stoppageSubTypeId,
      incident: false,
    }))

    if (!response.data) continue

    const row = new StoppageDto({
      ...response.data,
      plant: (batch.machines?.find(machine => machine.id === plantId)
        ?? { id: plantId } as PlantDTO) as StoppageDto['plant'],
      stoppageSubType: new StoppageSubTypeDto({
        stoppageSubTypeId: batch.stoppageSubTypeId,
        stoppageSubType: batch.subType?.label ?? '',
        stoppageTypeId: batch.stoppageTypeId,
      }),
      stoppageType: new StoppageTypeDto({
        stoppageTypeId: batch.stoppageTypeId,
        stoppageType: batch.subType?.stoppageType ?? '',
        colourHex: batch.subType?.groupColor,
      }),
    })

    appendCachedStoppage(row, dateIso, batch.selectedShift)
    appendCachedPlantStoppage(row, dateIso, batch.selectedShift, plantId)

    if (!batch.timeOut) {
      patchCachedOperationStatus(plantId, dateIso, batch.selectedShift, {
        status: batch.subType?.stoppageType ?? 'Maintenance Down',
        statusDescription: `${batch.subType?.stoppageType ?? ''} - ${batch.subType?.label ?? ''}`.trim(),
        stoppageColourHub: batch.subType?.groupColor,
        stoppageId: response.data.stoppageId,
        currentStoppageTypeId: batch.stoppageTypeId,
        currentStoppageSubTypeId: batch.stoppageSubTypeId,
        timeIn: batch.timeIn,
      })
    }

    added++
  }

  return added
}
